import React, { useState } from 'react';
import { Box, ButtonBase, Collapse, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import BlockIcon from '@mui/icons-material/Block';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { ToolCallStatus, ToolCallSummary } from '../../../services/assistantCentral';
import { ASSISTANT_THEME } from '../assistantTheme';

const STATUS_ICON: Record<ToolCallStatus, React.ReactElement> = {
  ok: <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />,
  error: <ErrorOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />,
  denied: <BlockIcon sx={{ fontSize: 16, color: 'warning.main' }} />,
};

const STATUS_LABEL: Record<ToolCallStatus, string> = {
  ok: 'Verificado',
  error: 'Falhou',
  denied: 'Sem permissão',
};

interface AssistantToolCallsProps {
  toolCalls: ToolCallSummary[];
  defaultOpen?: boolean;
}

const AssistantToolCalls: React.FC<AssistantToolCallsProps> = ({ toolCalls, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  if (!toolCalls.length) return null;

  return (
    <Box sx={{ mt: 0.75 }}>
      <ButtonBase
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          borderRadius: 1,
          px: 0.5,
          py: 0.25,
          fontSize: '0.72rem',
          fontWeight: 600,
          color: ASSISTANT_THEME.tealDark,
        }}
      >
        O que eu verifiquei ({toolCalls.length})
        <ExpandMoreIcon
          sx={{ fontSize: 16, transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none' }}
        />
      </ButtonBase>
      <Collapse in={open} unmountOnExit>
        <Box
          component="ul"
          sx={{
            listStyle: 'none',
            m: 0,
            mt: 0.5,
            p: 0.75,
            borderRadius: 1.5,
            bgcolor: 'rgba(255,255,255,0.7)',
            border: `1px solid ${ASSISTANT_THEME.borderSoft}`,
          }}
        >
          {toolCalls.map((call, idx) => (
            <Box component="li" key={`${call.tool}-${idx}`} sx={{ display: 'flex', gap: 0.75, py: 0.4 }}>
              <Box sx={{ pt: '1px' }} title={STATUS_LABEL[call.status] || ''}>
                {STATUS_ICON[call.status] || STATUS_ICON.ok}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: ASSISTANT_THEME.navy }}>
                  {call.label}
                </Typography>
                {call.summary && (
                  <Typography sx={{ fontSize: '0.72rem', color: ASSISTANT_THEME.slateMid }}>{call.summary}</Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default AssistantToolCalls;
